import { useEffect, useState } from 'react';
import { db } from '../instant';
import { timeout } from '../utils';
import { isLocalhost } from '../serviceWorkerRegistration';
import { useEventsList } from '../context';
import { queryItemTypes } from '../schema';
import { IMAGE_CACHE_NAME, IMAGE_CACHE_ORIGINS } from '../constants';
import DownloadNotifier, { DownloadStatus } from './DownloadNotifier';

/** Number of ms to show "Finished" before auto-closing the banner. */
const FINISHED_DISPLAY_MS = 2000;

/** True if the URL is from one of our backends (dev or prod) that we cache. */
function isCacheableOrigin(url: string): boolean {
    try {
        return IMAGE_CACHE_ORIGINS.includes(new URL(url).origin);
    } catch {
        return false;
    }
}

// Check whether an image is present in the cache
const isCached = async (imageUrl: string): Promise<boolean> => {
    const cache = await window.caches.open(IMAGE_CACHE_NAME);
    const response = await cache.match(imageUrl);
    const isPresent = response !== undefined;
    return isPresent;
};

export default function CacheUpdater() {
    const events = useEventsList();
    const { data: eventsWithMapsData } = db.useQuery({ events: { maps: {} } });
    const { data: itemTypesData } = queryItemTypes();
    const { user } = db.useAuth();
    const token = user?.refresh_token;

    // Whether to show the loading indicator
    const [loadingStatus, setLoadingStatus] = useState<DownloadStatus | null>(null);

    // Query station types to cache icon info
    db.useQuery({ stationTypes: {} });

    const maybeDownloadOneImage = async (url: string, _token: string) => {
        const alreadyCached = await isCached(url);
        if (alreadyCached) return;
        try {
            const downloadPromise = fetch(url).then(async resp => {
                await resp.arrayBuffer();
                return resp;
            });
            const timeoutPromise = timeout(10000).then(() => {
                throw new Error('Image download timeout');
            });
            await Promise.race([downloadPromise, timeoutPromise]);
        } catch {
            // Ignore per-URL failures (timeout, 404, etc.)
        }
    };

    const eventsWithMaps = eventsWithMapsData?.events ?? [];
    const mapBgUrls = eventsWithMaps.flatMap(e => (e.maps ?? []).map((m: { bgImageUrl?: string | null }) => m.bgImageUrl).filter(Boolean));
    const itemTypePhotoUrls = (itemTypesData?.itemTypes ?? []).map(it => it.photoUrl).filter(Boolean);

    const rawUrls = [
        ...events.flatMap(event => [event.bgImageUrl, ...(event.clientLogoUrl ? [event.clientLogoUrl] : [])]),
        ...mapBgUrls,
        ...itemTypePhotoUrls,
    ].filter((url): url is string => url !== null && url !== undefined);

    // Only preload/cache images from our backends (dev and prod). Other origins are ignored.
    const imageUrls = [...new Set(rawUrls.filter(isCacheableOrigin))].sort();

    // Lists aren't good for useEffect dependencies
    // see https://stackoverflow.com/q/59467758/4228052
    const imageUrlsString = JSON.stringify(imageUrls);

    // Preload event map images, client logos, and inventory photos for offline use
    // fetching an image automatically causes
    // the service worker to cache it.
    useEffect(() => {
        if (window.caches === undefined) return;
        if (!navigator.onLine) return;

        const imageUrls: string[] = JSON.parse(imageUrlsString);
        if (!imageUrls.length || !token || isLocalhost) return;

        const fetchAllImages = async () => {
            // If everything is already cached, skip work and never show the banner.
            // This matters because CacheUpdater remounts on every navigation (it lives
            // inside each screen’s layout), so we’d otherwise run and sometimes show
            // the banner on every page change.
            const cacheChecks = await Promise.all(imageUrls.map(url => isCached(url)));
            const allCached = cacheChecks.every(Boolean);

            if (allCached) return;

            setLoadingStatus('Downloading');
            await Promise.all(imageUrls.map(url => maybeDownloadOneImage(url, token)));
            setLoadingStatus('Finished');
            await timeout(FINISHED_DISPLAY_MS);
            setLoadingStatus(null);
        };

        fetchAllImages().catch(() => {
            setLoadingStatus(null);
        });
    }, [imageUrlsString, token]);

    return <DownloadNotifier status={loadingStatus} onClose={() => setLoadingStatus(null)} />;
}
